import React, { Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { store, RootState, AppDispatch } from './store';
import { initializeAuth, clearAuthState } from './store/slices/authSlice';
import { setStoreDispatch } from './api';
import LoadingSpinner from './components/LoadingSpinner';

// 항상 로드 — 레이아웃, 인증, 알림
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { ChatWidget } from './components/chat';
import NotificationContainer from './components/NotificationContainer';
import { ConfirmProvider } from './hooks/useConfirm';

// 홈 페이지 (초기 랜딩이므로 정적 로드)
import GlobalNetwork from './components/home/GlobalNetwork';
import Philosophy from './pages/about/Philosophy';

// Lazy-loaded 페이지
const RequestSample = React.lazy(() => import('./pages/RequestSample'));
const Login = React.lazy(() => import('./pages/Login'));
const OAuthCallback = React.lazy(() => import('./pages/OAuthCallback'));
const RequestDetailRouter = React.lazy(() => import('./pages/RequestDetailRouter'));
const NDAContract = React.lazy(() => import('./pages/brand/NDAContract'));
const AdminDashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const AgentDashboard = React.lazy(() => import('./pages/admin/AgentDashboard'));
const GmailOAuthCallback = React.lazy(() => import('./pages/admin/GmailOAuthCallback'));
const McpAuthorizePage = React.lazy(() => import('./pages/admin/McpAuthorizePage'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const TermsOfService = React.lazy(() => import('./pages/legal/TermsOfService'));
const PrivacyPolicy = React.lazy(() => import('./pages/legal/PrivacyPolicy'));
const ShowroomPage = React.lazy(() => import('./pages/showroom/ShowroomPage'));
const FormulationsPage = React.lazy(() => import('./pages/formulations/FormulationsPage'));

// Suspense fallback
const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

// 메인 앱 컴포넌트 (Provider 내부)
const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, isInitialized, isAuthenticated, userType } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  // 공개 쇼룸(/showroom) + embed iframe(?embed=1, 예: D건 Preview)에는 내부 챗 위젯 미노출
  // — preview 내 챗 모달이 부모 앱의 실제 챗 모달과 중복 노출되는 문제 방지.
  // 샘플 요청서(/sample-request)도 미노출 — 바이어가 양식만 채우는 화면이라 챗 버튼을 띄우지 않습니다.
  const hideChatWidget =
    location.pathname.startsWith('/showroom') ||
    location.pathname.startsWith('/sample-request') ||
    new URLSearchParams(location.search).get('embed') === '1';

  useEffect(() => {
    // API layer에 Redux dispatch 연결
    setStoreDispatch(dispatch);
  }, [dispatch]);

  // 인증 초기화를 한 번만 실행하기 위한 ref
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Attempt to restore login state using refresh token on app start
    if (!hasInitialized.current && !isInitialized) {
      hasInitialized.current = true;
      // preview/embed iframe(?preview=1 | ?embed=1)에서는 refresh 를 하지 않음 —
      // iframe 의 /auth/refresh 가 부모 관리자 refreshToken 과 rotation 경합을 일으켜 로그아웃 유발.
      // 미인증(공개)으로 초기화만 함 (쇼룸 preview 는 sr_session, D건 embed 는 공개 목록·상세로 동작).
      const params = new URLSearchParams(location.search);
      if (params.get('embed') === '1' || params.get('preview') === '1') {
        dispatch(clearAuthState());
      } else {
        dispatch(initializeAuth());
      }
    }
  }, [dispatch, isInitialized, location.search]);

  // 초기화 중 로딩 화면 — 인증 무관 공개 카탈로그(D건)는 대기 없이 즉시 렌더(크롤러 첫 화면 지연 방지)
  const publicCatalog = location.pathname === '/formulations' || location.pathname.startsWith('/formulations/');
  if (!isInitialized && isLoading && !publicCatalog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Initializing..." />
      </div>
    );
  }

  return (
    <div className="App component-transition">
      <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Home page - GlobalNetwork and Philosophy combined */}
        <Route path="/" element={
          <Layout>
            <div className="min-h-screen bg-white">
              {/* <CEO embedded /> */}
              <GlobalNetwork embedded />
              <Philosophy embedded />
            </div>
          </Layout>
        } />

        {/* Public sample-request routes - accessible to all */}
        <Route path="/request" element={<Navigate to="/sample-request" replace />} />
        <Route path="/sample-request" element={
          <Layout>
            <RequestSample />
          </Layout>
        } />
        <Route path="/login" element={
          <Layout>
            {/* py-6 — 모바일 키보드로 뷰포트가 줄 때 카드 상하가 잘리지 않도록 */}
            <div className="min-h-full flex items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
              <Login />
            </div>
          </Layout>
        } />

        {/* OAuth 콜백 라우트 */}
        <Route path="/oauth/callback/:provider" element={<OAuthCallback />} />

        {/* 쇼룸 공개 페이지 — 자체 레이아웃 (관리자 발급 링크 ?t= / preview 세션으로만 접근) */}
        <Route path="/showroom" element={<ShowroomPage />} />

        {/* D건(New Formulations) — 전체 공개. 목록·상세가 한 화면(좌우 분할)이라 두 경로가 같은 컴포넌트 */}
        <Route path="/formulations" element={
          <Layout>
            <FormulationsPage />
          </Layout>
        } />
        <Route path="/formulations/:slug" element={
          <Layout>
            <FormulationsPage />
          </Layout>
        } />

        {/* Legacy routes - redirect to unified sample-request page */}
        <Route path="/dashboard" element={
          <Navigate to="/sample-request?tab=projects" replace />
        } />
        {/* Request detail page - accessible to both brand and admin users */}
        <Route path="/requests/:id" element={
          <ProtectedRoute>
            <Layout>
              <RequestDetailRouter />
            </Layout>
          </ProtectedRoute>
        } />

        {/* NDA Contract - accessible to brand users */}
        <Route path="/contract/nda" element={
          <ProtectedRoute userType="brand">
            <Layout>
              <NDAContract />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/contracts/nda" element={
          <ProtectedRoute userType="brand">
            <Layout>
              <NDAContract />
            </Layout>
          </ProtectedRoute>
        } />

        {/* Gmail OAuth callback (Connected Services) */}
        <Route path="/admin/google/callback" element={
          <ProtectedRoute userType="admin">
            <GmailOAuthCallback />
          </ProtectedRoute>
        } />

        {/* Admin routes - protected and separate */}
        <Route path="/admin/dashboard" element={
          <Navigate to="/admin/management" replace />
        } />
        <Route path="/admin/management/*" element={
          <ProtectedRoute userType="admin">
            <Layout>
              <AdminDashboard initialTab="management" />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/admin/settings/*" element={
          <ProtectedRoute userType="admin">
            <Layout>
              <AdminDashboard initialTab="admin" />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/admin/agent/*" element={
          <ProtectedRoute userType="admin">
            <Layout>
              <AgentDashboard />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/admin/mcp-authorize" element={
          <ProtectedRoute userType="admin">
            <McpAuthorizePage />
          </ProtectedRoute>
        } />

        {/* Legal pages */}
        <Route path="/terms" element={
          <Layout>
            <TermsOfService />
          </Layout>
        } />
        <Route path="/privacy" element={
          <Layout>
            <PrivacyPolicy />
          </Layout>
        } />

        {/* 404 */}
        <Route path="*" element={
          <Layout>
            <NotFound />
          </Layout>
        } />
      </Routes>
      </Suspense>

      {!hideChatWidget && <ChatWidget isAuthenticated={isAuthenticated} />}
      {isAuthenticated && <NotificationContainer />}
    </div>
  );
};

// 최상위 App 컴포넌트 (Redux Provider 포함)
const App: React.FC = () => {
  return (
    <Provider store={store}>
      <ConfirmProvider>
        <AppContent />
      </ConfirmProvider>
    </Provider>
  );
};

export default App;